/**
 * Stock-Build / Idle-Run Authorisation — MTS workflow.
 * ─────────────────────────────────────────────────────────────────────────────
 * For ~10% of production volume there is no customer Sales Order but
 * machines must run anyway. Two distinct triggers, both requiring
 * management e-sign:
 *
 *   MTS_STRATEGIC   Market expected to rise — produce stock now
 *                   (MD signs)
 *   MTS_IDLE        No orders, machines must run for cost amortisation
 *                   (GM signs)
 *
 * Lifecycle: DRAFT → AUTHORISED → CONSUMED (becomes internal SO).
 *            Or DRAFT/AUTHORISED → CANCELLED.
 *
 * Once consumed, an internal Sales Order is created (so_no like
 * SO-INTERNAL-2026-0001) which then flows through the normal Production
 * Campaign / Mixing Plan / Laydown machinery.
 */
import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, Drawer, Empty, Form, Input,
  InputNumber, Popconfirm, Row, Select, Space, Statistic, Table,
  Tag, Typography, DatePicker, Divider,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  EditOutlined, FileTextOutlined, PlusOutlined, RocketOutlined,
  SafetyCertificateOutlined, StopOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

interface StockBuildAuth {
  id: string
  auth_no: string
  auth_date: string
  count_ne: string
  yarn_type: string
  end_use: string | null
  quantity_kg: string
  delivery_target_date: string | null
  origin_code: string
  reason: string
  market_view: string | null
  estimated_cost_per_kg_inr: string | null
  expected_sale_rate_per_kg_inr: string | null
  expected_value_per_kg_inr: string | null
  status: string
  authorised_by: string | null
  authorised_at: string | null
  consumed_so_id: string | null
  notes: string | null
  created_at: string | null
}

const STATUS_TAG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  DRAFT:      { color: 'default',   icon: <ClockCircleOutlined />,        label: 'Draft' },
  AUTHORISED: { color: 'orange',    icon: <SafetyCertificateOutlined />,  label: 'Authorised' },
  CONSUMED:   { color: 'green',     icon: <CheckCircleOutlined />,        label: 'Consumed' },
  CANCELLED:  { color: 'red',       icon: <CloseCircleOutlined />,        label: 'Cancelled' },
}

const ORIGIN_TAG: Record<string, { color: string; label: string; tooltip: string }> = {
  MTS_STRATEGIC: { color: 'purple', label: 'Strategic Stock Build',
    tooltip: 'Market expected to rise — produce now, sell later. MD signs.' },
  MTS_IDLE: { color: 'gold', label: 'Idle Capacity Run',
    tooltip: 'No orders, machines must run to amortise cost. GM signs.' },
}

const YARN_TYPES = ['CARDED', 'CARDED_COMPACT', 'COMPACT', 'SIRO', 'COMBED', 'COMBED_COMPACT']
const COUNTS = [8, 10, 12, 16, 20, 24, 30, 32, 34, 40, 45, 50, 60, 70, 80, 90, 100, 110, 120]

export default function StockBuildAuthPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [originFilter, setOriginFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<StockBuildAuth | null>(null)
  const [form] = Form.useForm()

  const { data: auths = [], isLoading } = useQuery<StockBuildAuth[]>({
    queryKey: ['stock-build-auths', plantId, statusFilter, originFilter],
    queryFn: () =>
      apiClient.get('/stock-build-authorisations', {
        params: { plant_id: plantId, status: statusFilter, origin_code: originFilter },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (body: any) =>
      apiClient.post('/stock-build-authorisations', body).then(r => r.data),
    onSuccess: () => {
      message.success('Authorisation drafted ✓')
      qc.invalidateQueries({ queryKey: ['stock-build-auths', plantId] })
      setDrawerOpen(false); form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      apiClient.patch(`/stock-build-authorisations/${id}`, body).then(r => r.data),
    onSuccess: () => {
      message.success('Updated ✓')
      qc.invalidateQueries({ queryKey: ['stock-build-auths', plantId] })
      setDrawerOpen(false); setEditing(null); form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Update failed'),
  })

  const lifecycleMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiClient.post(`/stock-build-authorisations/${id}/${action}`).then(r => r.data),
    onSuccess: (_, vars) => {
      const labels: Record<string, string> = {
        sign: 'Authorisation signed ✓',
        cancel: 'Authorisation cancelled',
        consume: 'Internal Sales Order created — production can begin',
      }
      message.success(labels[vars.action] ?? `${vars.action} done`)
      qc.invalidateQueries({ queryKey: ['stock-build-auths', plantId] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Action failed'),
  })

  const openCreate = () => {
    setEditing(null); form.resetFields()
    form.setFieldsValue({
      auth_date: dayjs(),
      origin_code: 'MTS_STRATEGIC',
      shifts_per_day: 3,
    })
    setDrawerOpen(true)
  }

  const openEdit = (a: StockBuildAuth) => {
    setEditing(a)
    form.setFieldsValue({
      ...a,
      auth_date: dayjs(a.auth_date),
      delivery_target_date: a.delivery_target_date ? dayjs(a.delivery_target_date) : null,
    })
    setDrawerOpen(true)
  }

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      const body = {
        plant_id: plantId,
        ...vals,
        auth_date: vals.auth_date?.format('YYYY-MM-DD'),
        delivery_target_date: vals.delivery_target_date?.format('YYYY-MM-DD') || null,
      }
      if (editing) {
        // Editing — exclude immutable fields
        const { plant_id: _p, ...patch } = body
        void _p
        updateMut.mutate({ id: editing.id, body: patch })
      } else {
        createMut.mutate(body)
      }
    })
  }

  // KPIs
  const draftCount = auths.filter(a => a.status === 'DRAFT').length
  const authorisedCount = auths.filter(a => a.status === 'AUTHORISED').length
  const consumedCount = auths.filter(a => a.status === 'CONSUMED').length
  const totalKg = auths.filter(a => a.status !== 'CANCELLED')
    .reduce((s, a) => s + Number(a.quantity_kg || 0), 0)

  const columns: ColumnsType<StockBuildAuth> = [
    { title: 'Auth No', dataIndex: 'auth_no', key: 'an', width: 140,
      render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'Origin', dataIndex: 'origin_code', key: 'oc', width: 160,
      render: (v: string) => {
        const cfg = ORIGIN_TAG[v]
        return cfg ? <Tag color={cfg.color} title={cfg.tooltip} style={{ fontSize: 10 }}>{cfg.label}</Tag>
          : <Tag>{v}</Tag>
      } },
    { title: 'What', key: 'what', width: 180,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
          <span><b>{Number(r.count_ne).toFixed(0)}s {r.yarn_type.replace(/_/g, ' ')}</b></span>
          {r.end_use && <Text type="secondary">{r.end_use}</Text>}
        </Space>
      ) },
    { title: 'Qty', dataIndex: 'quantity_kg', key: 'q', width: 100, align: 'right',
      render: (v: string) => <Text strong>{Number(v).toFixed(0)} kg</Text> },
    { title: 'Sale Rate', dataIndex: 'expected_sale_rate_per_kg_inr', key: 'sr', width: 110, align: 'right',
      render: (v: string | null) => v ? <span>₹{Number(v).toFixed(0)}/kg</span> : '—' },
    { title: 'Auth Date', dataIndex: 'auth_date', key: 'ad', width: 110,
      render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text> },
    { title: 'Status', dataIndex: 'status', key: 's', width: 130,
      render: (v: string) => {
        const cfg = STATUS_TAG[v] ?? { color: 'default', label: v, icon: null }
        return <Tag icon={cfg.icon} color={cfg.color}>{cfg.label}</Tag>
      } },
    { title: 'Reason', dataIndex: 'reason', key: 'r', width: 240, ellipsis: true,
      render: (v: string) => <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: v }}>{v}</Text> },
    { title: 'Action', key: 'act', width: 220, fixed: 'right' as const,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {r.status === 'DRAFT' && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          )}
          {r.status === 'DRAFT' && (
            <Popconfirm title="Sign and authorise?"
              description="Once signed, edits are locked. The reason text becomes the audit-of-record."
              onConfirm={() => lifecycleMut.mutate({ id: r.id, action: 'sign' })}>
              <Button size="small" type="primary" icon={<SafetyCertificateOutlined />}>
                Sign
              </Button>
            </Popconfirm>
          )}
          {r.status === 'AUTHORISED' && (
            <Popconfirm title="Create internal Sales Order from this authorisation?"
              description="An SO with so_no=SO-INTERNAL-* will be created. Production can begin via Production Campaign."
              onConfirm={() => lifecycleMut.mutate({ id: r.id, action: 'consume' })}>
              <Button size="small" type="primary" icon={<RocketOutlined />}>
                Consume → Create SO
              </Button>
            </Popconfirm>
          )}
          {(r.status === 'DRAFT' || r.status === 'AUTHORISED') && (
            <Popconfirm title="Cancel this authorisation?"
              onConfirm={() => lifecycleMut.mutate({ id: r.id, action: 'cancel' })}>
              <Button size="small" type="link" danger icon={<StopOutlined />}>Cancel</Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ]

  return (
    <div>
      <PageHeader
        title="Stock-Build Authorisations"
        subtitle="MTS — Make-to-Stock when there's no customer order. Strategic (MD signs) or Idle (GM signs)."
        extra={
          <Space>
            <Select allowClear placeholder="Filter status"
              style={{ width: 140 }} value={statusFilter} onChange={setStatusFilter}
              options={Object.keys(STATUS_TAG).map(k => ({
                value: k, label: STATUS_TAG[k].label,
              }))} />
            <Select allowClear placeholder="Filter origin"
              style={{ width: 180 }} value={originFilter} onChange={setOriginFilter}
              options={[
                { value: 'MTS_STRATEGIC', label: 'Strategic' },
                { value: 'MTS_IDLE', label: 'Idle Capacity' },
              ]} />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Authorisation
            </Button>
          </Space>
        }
      />

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #999' }}>
            <Statistic title="Drafts" value={draftCount}
              valueStyle={{ fontSize: 22, fontWeight: 700 }}
              prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
            <Statistic title="Awaiting Sign" value={authorisedCount}
              valueStyle={{ fontSize: 22, color: '#faad14', fontWeight: 700 }}
              prefix={<SafetyCertificateOutlined />} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
            <Statistic title="Consumed" value={consumedCount}
              valueStyle={{ fontSize: 22, color: '#52c41a', fontWeight: 700 }}
              prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #1677ff' }}>
            <Statistic title="Total Volume" value={totalKg.toFixed(0)} suffix="kg"
              valueStyle={{ fontSize: 22, color: '#1677ff', fontWeight: 700 }} />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        {!plantId ? <Empty description="Select a plant first" />
          : <Table rowKey="id" size="small" loading={isLoading}
              dataSource={auths} columns={columns}
              scroll={{ x: 1200 }}
              pagination={{ pageSize: 25, showTotal: t => `${t} authorisations` }} />}
      </Card>

      <Drawer
        title={
          <Space>
            <FileTextOutlined style={{ color: '#1677ff' }} />
            {editing ? `Edit Auth — ${editing.auth_no}` : 'New Stock-Build Authorisation'}
          </Space>
        }
        open={drawerOpen}
        width={600}
        onClose={() => { setDrawerOpen(false); setEditing(null); form.resetFields() }}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit}
              loading={createMut.isPending || updateMut.isPending}>
              {editing ? 'Save Changes' : 'Save as Draft'}
            </Button>
          </Space>
        }
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Stock-Build is for production WITHOUT a customer order. Once signed, an internal Sales Order is created and production proceeds normally." />

        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="origin_code" label="Origin"
                rules={[{ required: true }]}>
                <Select options={[
                  { value: 'MTS_STRATEGIC', label: 'Strategic — Market expected to rise (MD signs)' },
                  { value: 'MTS_IDLE', label: 'Idle Capacity — No orders, machines must run (GM signs)' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="auth_date" label="Authorisation Date"
                rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 12 }}>What to Produce</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="count_ne" label="Count (Ne)"
                rules={[{ required: true }]}>
                <Select options={COUNTS.map(c => ({ value: c, label: `${c}s` }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="yarn_type" label="Yarn Type"
                rules={[{ required: true }]}>
                <Select options={YARN_TYPES.map(t => ({ value: t, label: t.replace(/_/g, ' ') }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="end_use" label="End Use">
                <Select allowClear options={[
                  { value: 'KNITTING', label: 'Knitting' },
                  { value: 'WEAVING', label: 'Weaving' },
                  { value: 'DYEING', label: 'Dyeing' },
                  { value: 'INDUSTRIAL', label: 'Industrial' },
                ]} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="quantity_kg" label="Quantity (kg)"
                rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} addonAfter="kg" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="delivery_target_date" label="Target Completion Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 12 }}>
            Cost & Sale Forecast (frozen at sign time)
          </Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="estimated_cost_per_kg_inr" label="Est. Cost ₹/kg">
                <InputNumber style={{ width: '100%' }} min={0} addonBefore="₹" precision={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expected_sale_rate_per_kg_inr" label="Sale Rate ₹/kg">
                <InputNumber style={{ width: '100%' }} min={0} addonBefore="₹" precision={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expected_value_per_kg_inr" label="Expected Total ₹">
                <InputNumber style={{ width: '100%' }} min={0} addonBefore="₹" precision={2} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 12 }}>Justification (signed-off context)</Divider>
          <Form.Item name="reason" label="Reason"
            rules={[{ required: true, min: 10, message: 'Minimum 10 chars — be specific' }]}>
            <Input.TextArea rows={3}
              placeholder="e.g. Cotton prices expected to rise 10-15% over next 6 weeks per CCI report. Build 5T 30s carded compact stock now to lock margin." />
          </Form.Item>
          <Form.Item name="market_view" label="Market View">
            <Input.TextArea rows={2}
              placeholder="e.g. CCI Apr-26 monthly: yarn export forecast +12%" />
          </Form.Item>
          <Form.Item name="notes" label="Internal Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
